import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';

import { Badge } from './ui/badge';
import { useSectionAnimationContext } from '../contexts/SectionAnimationContext';
import { ChevronRight, Building, Award, TrendingUp, Users, Code, BookOpen, Target } from 'lucide-react';
import { personal_content } from '../data/content';
import LiquidBlobs from './LiquidBlobs';

const PersonalSection = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const { getSectionClasses, isSectionVisible } = useSectionAnimationContext();

  // Use static data instead of API call
  const personalContent = personal_content[0] || null;

  return (
    <section id="personal" className="py-24 bg-gradient-to-b from-gray-100 to-gray-50 relative overflow-hidden">
      <LiquidBlobs section="personal" />
      <div className="container mx-auto px-6 relative z-10">
        {/* Section content with genie animation */}
        <div className={getSectionClasses('personal')}>
        {/* Section Header with Creative Typography */}
        <div className="mb-16 text-center">
          <h2 className="heading-1 text-5xl md:text-7xl font-black text-text-primary mb-4 font-grotesk uppercase">
            {(personalContent?.section_title || "About Gideon") === "About Gideon" ? (
              <>About <span className="text-red-600">Gideon</span></>
            ) : (
              personalContent?.section_title || "About Gideon"
            )}
          </h2>
          <div className="w-24 h-1 bg-red-600 mx-auto mb-6"></div>
          <p className="body-medium text-text-secondary max-w-3xl mx-auto">
            {personalContent?.section_subtitle || "A results-driven professional with ~15 years of transformative experience in asset management, AI implementation, and building high-performance teams that deliver exceptional results."}
          </p>
        </div>

        {/* Tabs Navigation - Button Style like Tech Projects */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {[
            { value: 'overview', label: 'Overview' },
            { value: 'experience', label: 'Experience' },
            { value: 'skills', label: 'Skills' },
            { value: 'expertise', label: 'Expertise' },
            { value: 'achievements', label: 'Achievements' }
          ].map((tab) => (
            <Button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={`btn-base ${
                activeTab === tab.value 
                  ? 'bg-red-600 text-white border-red-600 hover:bg-red-700 hover:border-red-700 transform scale-110 shadow-lg' 
                  : 'bg-transparent text-gray-900 border-gray-900 hover:bg-gray-900 hover:text-white hover:scale-105 hover:shadow-md'
              } transition-all duration-300 hover:transform hover:-translate-y-2`}
            >
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Tabs Content Container */}
        <div className="w-full">
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* Professional Profile Section with Photo */}
              <div className="grid md:grid-cols-3 gap-8 mb-8">
                <div className="md:col-span-1">
                  <Card className="glass-card overflow-hidden">
                    <div className="relative">
                      <img 
                        src={personalContent?.profile_image || "https://customer-assets.emergentagent.com/job_metreprofile/artifacts/vuwppivf_Gideon%20Working%20on%20wide%20screen%20%26%20bbg%20keyboard.jpg"}
                        alt="Gideon Metre - Professional Headshot"
                        className="w-full h-80 object-cover object-center"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                    </div>
                    <div className="p-6 text-center">
                      <h3 className="heading-4 text-text-primary mb-2">Gideon Metre</h3>
                      <p className="text-text-secondary text-sm">Senior Investment Management Leader</p>
                      <p className="text-text-secondary text-sm">AI Implementation Sponsor</p>
                    </div>
                  </Card>
                </div>
                
                <div className="md:col-span-2">
                  <Card className="liquid-glass glass-card p-8 h-full">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                        <Award className="text-green-600" size={24} />
                      </div>
                      <h3 className="heading-4 text-text-primary">Professional Summary</h3>
                    </div>
                    <p className="body-medium text-text-secondary mb-4">
                      Investment management leader with ~15 years of progressive experience building and running
                      front-office portfolio management, collateral management, fund administration, and investment operations
                      for global asset managers. Proven track record of establishing greenfield GCC functions from zero,
                      scaling cross-functional teams, and delivering measurable impact across equities, FX, derivatives, and multi-asset mandates.
                    </p>
                    <p className="body-medium text-text-secondary mb-6">
                      Currently leading 19+ professionals across 9 cross-functional teams at Russell Investments as Associate Director,
                      serving as local execution leader and thought partner to the Global MD. Entrusted by the firm as India AI Management
                      Sponsor to drive business-side AI adoption.
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">1600+</div>
                        <div className="text-sm text-blue-700">FTE Hours Saved</div>
                      </div>
                      <div className="text-center p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">98%</div>
                        <div className="text-sm text-green-700">Team Engagement</div>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="liquid-glass glass-card p-8">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                    <Users className="text-blue-600" size={24} />
                  </div>
                  <h3 className="heading-4 text-text-primary">Leadership Excellence</h3>
                </div>
                <p className="body-small text-text-secondary mb-4">
                  Currently leading 19+ professionals across 9 cross-functional teams at Russell Investments,
                  reporting directly to Global MD with responsibility for front-office Implementation Portfolio Management.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Badge className="bg-blue-100 text-blue-700 border-blue-200">Greenfield GCC Build</Badge>
                  <Badge className="bg-blue-100 text-blue-700 border-blue-200">Strategic Leadership</Badge>
                  <Badge className="bg-blue-100 text-blue-700 border-blue-200">Global Management</Badge>
                </div>
              </Card>

              <Card className="liquid-glass glass-card p-8">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mr-4">
                    <Code className="text-purple-600" size={24} />
                  </div>
                  <h3 className="heading-4 text-text-primary">AI & Automation Pioneer</h3>
                </div>
                <p className="body-small text-text-secondary mb-4">
                  India AI Management Sponsor, deployed 40+ Alteryx workflows and 10+ VBA automation
                  projects, delivering 1600+ FTE hours of capacity savings with ~70% processing time reduction.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Badge className="bg-purple-100 text-purple-700 border-purple-200">Alteryx</Badge>
                  <Badge className="bg-purple-100 text-purple-700 border-purple-200">VBA</Badge>
                  <Badge className="bg-purple-100 text-purple-700 border-purple-200">Gen AI</Badge>
                  <Badge className="bg-purple-100 text-purple-700 border-purple-200">Process Automation</Badge>
                </div>
              </Card>
            </div>

            {/* Professional Journey Timeline */}
            <div className="liquid-glass glass-card p-8 rounded-lg">
              <h3 className="heading-3 text-text-primary mb-6 font-grotesk">Professional Journey</h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-4 h-4 bg-green-500 rounded-full mt-2 shadow-lg"></div>
                  <div>
                    <h4 className="font-semibold text-text-primary">Russell Investments - Associate Director</h4>
                    <p className="text-green-600 text-sm font-medium">Mar 2024 - Present</p>
                    <p className="text-text-secondary text-sm mt-1">Implementation Portfolio Management (Front Office) & India AI Management Sponsor</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-4 h-4 bg-blue-500 rounded-full mt-2 shadow-lg"></div>
                  <div>
                    <h4 className="font-semibold text-text-primary">TIAA GBS - Senior Associate/Manager</h4>
                    <p className="text-blue-600 text-sm font-medium">Jan 2019 - Mar 2024</p>
                    <p className="text-text-secondary text-sm mt-1">Investment Accounting & Reporting, Business Intelligence, Process Automation</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-4 h-4 bg-purple-500 rounded-full mt-2 shadow-lg"></div>
                  <div>
                    <h4 className="font-semibold text-text-primary">FIS (SunGard) - Senior Analyst</h4>
                    <p className="text-purple-600 text-sm font-medium">Feb 2017 - Jan 2019</p>
                    <p className="text-text-secondary text-sm mt-1">Post-Trade Derivatives Collateral Management</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-4 h-4 bg-amber-500 rounded-full mt-2 shadow-lg"></div>
                  <div>
                    <h4 className="font-semibold text-text-primary">BNY Mellon - Analyst</h4>
                    <p className="text-amber-600 text-sm font-medium">Nov 2013 - Feb 2017</p>
                    <p className="text-text-secondary text-sm mt-1">Prime Brokerage & Custody, Client Service</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-4 h-4 bg-gray-500 rounded-full mt-2 shadow-lg"></div>
                  <div>
                    <h4 className="font-semibold text-text-primary">EXL Services - Customer Support Representative</h4>
                    <p className="text-gray-600 text-sm font-medium">Aug 2010 - Oct 2011</p>
                    <p className="text-text-secondary text-sm mt-1">General Insurance Operations</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'experience' && (
          <div className="space-y-8">
            <div className="grid gap-6">
              <Card className="liquid-glass glass-card p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="heading-4 text-text-primary">Russell Investments (RIIV)</h3>
                    <p className="text-green-600 font-semibold">Associate Director - Implementation Portfolio Management (Front Office)</p>
                    <p className="text-text-secondary text-sm">Mar 2024 - Present | Mumbai</p>
                  </div>
                  <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center">
                    <Building className="text-green-600" size={32} />
                  </div>
                </div>
                <p className="body-small text-text-secondary mb-4">
                  Established Investment Division CPS department from ground-up. Leading 19+ professionals across 9 cross-functional teams
                  covering IPM, Currency/FX, Equity/EPI, Overlay Services, Investment Reporting, Portfolio Analysts,
                  Transition Management, PMA, and Security Lending.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <ChevronRight size={16} className="text-green-600 mr-2" />
                    <span className="text-text-secondary text-sm">Local execution leader and thought partner to Global MD; presents to CIO and COO</span>
                  </div>
                  <div className="flex items-center">
                    <ChevronRight size={16} className="text-green-600 mr-2" />
                    <span className="text-text-secondary text-sm">India AI Management Sponsor - chair of AI Mumbai Committee driving business-side AI adoption</span>
                  </div>
                  <div className="flex items-center">
                    <ChevronRight size={16} className="text-green-600 mr-2" />
                    <span className="text-text-secondary text-sm">Portfolio management for North America and EMEA clients; yearly travel to Seattle HQ and London</span>
                  </div>
                  <div className="flex items-center">
                    <ChevronRight size={16} className="text-green-600 mr-2" />
                    <span className="text-text-secondary text-sm">98% engagement scores, zero attrition since department formation</span>
                  </div>
                </div>
              </Card>

              <Card className="liquid-glass glass-card p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="heading-4 text-text-primary">TIAA Global Capability Centre</h3>
                    <p className="text-blue-600 font-semibold">Senior Associate / Manager</p>
                    <p className="text-text-secondary text-sm">Jan 2019 - Mar 2024 (5 yrs 2 months) | Pune</p>
                  </div>
                  <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Building className="text-blue-600" size={32} />
                  </div>
                </div>
                <p className="body-small text-text-secondary mb-4">
                  Established 4 new teams in Investment Centre of Excellence from scratch.
                  Led Securities, Derivatives, Mortgage, and Investment Reporting Warehouse teams.
                  Operated as 'client' overseeing outsourced business to State Street (SSKC).
                </p>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <ChevronRight size={16} className="text-blue-600 mr-2" />
                    <span className="text-text-secondary text-sm">Delivered 420+ FTE hours through Alteryx, VBA, and Power Automate automation</span>
                  </div>
                  <div className="flex items-center">
                    <ChevronRight size={16} className="text-blue-600 mr-2" />
                    <span className="text-text-secondary text-sm">~98% engagement scores, 0% attrition for 4 years; led quarterly townhalls during COVID-19</span>
                  </div>
                  <div className="flex items-center">
                    <ChevronRight size={16} className="text-blue-600 mr-2" />
                    <span className="text-text-secondary text-sm">Managed complete US-to-India business transition including knowledge transfer and go-live</span>
                  </div>
                </div>
              </Card>

              <Card className="liquid-glass glass-card p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="heading-4 text-text-primary">FIS Global (SunGard)</h3>
                    <p className="text-purple-600 font-semibold">Senior Analyst - Post-Trade Derivatives Collateral Management</p>
                    <p className="text-text-secondary text-sm">Feb 2017 - Jan 2019 (2 years) | Mumbai</p>
                  </div>
                  <div className="w-16 h-16 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Building className="text-purple-600" size={32} />
                  </div>
                </div>
                <p className="body-small text-text-secondary mb-4">
                  Managed OTC clearing and collateral management across cash and non-cash assets.
                  Acted as clearing member for CCPs (ICE, EUREX, LCH).
                </p>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <ChevronRight size={16} className="text-purple-600 mr-2" />
                    <span className="text-text-secondary text-sm">Administered initial margin for OTC derivatives and futures clients (EMIR compliance)</span>
                  </div>
                  <div className="flex items-center">
                    <ChevronRight size={16} className="text-purple-600 mr-2" />
                    <span className="text-text-secondary text-sm">Introduced new ISA product offerings; led client onboarding and account setup</span>
                  </div>
                  <div className="flex items-center">
                    <ChevronRight size={16} className="text-purple-600 mr-2" />
                    <span className="text-text-secondary text-sm">Key automation developer and VBA resource across Mumbai teams</span>
                  </div>
                </div>
              </Card>

              <Card className="liquid-glass glass-card p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="heading-4 text-text-primary">BNY Mellon</h3>
                    <p className="text-amber-600 font-semibold">Analyst - Prime Brokerage & Custody, Client Service</p>
                    <p className="text-text-secondary text-sm">Nov 2013 - Feb 2017 (3 yrs 7 months) | Pune</p>
                  </div>
                  <div className="w-16 h-16 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Building className="text-amber-600" size={32} />
                  </div>
                </div>
                <p className="body-small text-text-secondary mb-4">
                  Capital markets operations across equities, bonds, options, FX, SWAPs, futures, collaterals, CFDs, and private investments.
                  Coordination with prime brokers JP Morgan, Morgan Stanley, and UBS.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <ChevronRight size={16} className="text-amber-600 mr-2" />
                    <span className="text-text-secondary text-sm">Built VBA macro eliminating 1.5 FTE of manual workload; Employee of the Month</span>
                  </div>
                  <div className="flex items-center">
                    <ChevronRight size={16} className="text-amber-600 mr-2" />
                    <span className="text-text-secondary text-sm">Assisted setting up new process operations at Chennai office</span>
                  </div>
                  <div className="flex items-center">
                    <ChevronRight size={16} className="text-amber-600 mr-2" />
                    <span className="text-text-secondary text-sm">Promoted from temporary role to on-payroll FTE within 5 months, then to Analyst</span>
                  </div>
                </div>
              </Card>

              <Card className="liquid-glass glass-card p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="heading-4 text-text-primary">EXL Services</h3>
                    <p className="text-gray-600 font-semibold">Customer Support Representative - General Insurance</p>
                    <p className="text-text-secondary text-sm">Aug 2010 - Oct 2011 | Pune</p>
                  </div>
                  <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                    <Building className="text-gray-600" size={32} />
                  </div>
                </div>
                <p className="body-small text-text-secondary mb-4">
                  Encompass and Allstate Insurance Operations - general insurance, claims analysis,
                  document verification, policy processing across homeowners, auto, and commercial lines.
                </p>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'skills' && (
          <div className="space-y-8">
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="liquid-glass glass-card p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mr-4">
                    <Target className="text-emerald-600" size={24} />
                  </div>
                  <h3 className="heading-5 text-text-primary">Technical Skills</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {(personalContent?.skills?.technical || ['Alteryx Analytics', 'VBA Programming', 'SQL', 'Power BI', 'Python', 'Bloomberg Terminal']).map((skill, index) => (
                      <Badge key={index} className="bg-emerald-100 text-emerald-700 border-emerald-200">{skill}</Badge>
                    ))}
                  </div>
                </div>
              </Card>

              <Card className="liquid-glass glass-card p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mr-4">
                    <TrendingUp className="text-indigo-600" size={24} />
                  </div>
                  <h3 className="heading-5 text-text-primary">Domain Expertise</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {(personalContent?.skills?.domain || ['Portfolio Management', 'Asset Management', 'Derivatives', 'Risk Management', 'Investment Operations', 'Fund Administration']).map((skill, index) => (
                      <Badge key={index} className="bg-indigo-100 text-indigo-700 border-indigo-200">{skill}</Badge>
                    ))}
                  </div>
                </div>
              </Card>

              <Card className="liquid-glass glass-card p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center mr-4">
                    <Users className="text-rose-600" size={24} />
                  </div>
                  <h3 className="heading-5 text-text-primary">Leadership Skills</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {(personalContent?.skills?.leadership || ['Team Building', 'Strategic Planning', 'Cross-functional Leadership', 'Process Improvement', 'Stakeholder Management', 'Performance Management']).map((skill, index) => (
                      <Badge key={index} className="bg-rose-100 text-rose-700 border-rose-200">{skill}</Badge>
                    ))}
                  </div>
                </div>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'expertise' && (
          <div className="space-y-8">
            <Card className="liquid-glass glass-card p-8 text-center">
              <div className="mb-6">
                <h3 className="heading-2 text-text-primary mb-4 font-grotesk">Core Expertise Areas</h3>
                <p className="body-medium text-text-secondary max-w-2xl mx-auto">
                  Key specializations and domain expertise that drive innovation and exceptional results in investment management and technology.
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="flex flex-col items-center p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg hover:shadow-lg transition-all duration-300">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mb-4">
                    <Code className="text-white" size={24} />
                  </div>
                  <h4 className="heading-6 text-text-primary mb-2">AI Implementation</h4>
                  <p className="text-sm text-text-secondary text-center">Leading AI initiatives and automation projects across investment operations</p>
                </div>

                <div className="flex flex-col items-center p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-lg hover:shadow-lg transition-all duration-300">
                  <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mb-4">
                    <TrendingUp className="text-white" size={24} />
                  </div>
                  <h4 className="heading-6 text-text-primary mb-2">Portfolio Management</h4>
                  <p className="text-sm text-text-secondary text-center">Managing portfolios for North America and EMEA clients with strategic oversight</p>
                </div>

                <div className="flex flex-col items-center p-6 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg hover:shadow-lg transition-all duration-300">
                  <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mb-4">
                    <Target className="text-white" size={24} />
                  </div>
                  <h4 className="heading-6 text-text-primary mb-2">Process Automation</h4>
                  <p className="text-sm text-text-secondary text-center">Streamlining operations through advanced automation and workflow optimization</p>
                </div>

                <div className="flex flex-col items-center p-6 bg-gradient-to-br from-red-50 to-red-100 rounded-lg hover:shadow-lg transition-all duration-300">
                  <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mb-4">
                    <Users className="text-white" size={24} />
                  </div>
                  <h4 className="heading-6 text-text-primary mb-2">Team Leadership</h4>
                  <p className="text-sm text-text-secondary text-center">Building and managing cross-functional teams with 98% engagement scores</p>
                </div>

                <div className="flex flex-col items-center p-6 bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg hover:shadow-lg transition-all duration-300">
                  <div className="w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center mb-4">
                    <Building className="text-white" size={24} />
                  </div>
                  <h4 className="heading-6 text-text-primary mb-2">Investment Operations</h4>
                  <p className="text-sm text-text-secondary text-center">Managing complex investment operations, post-trade processes, and fund administration</p>
                </div>

                <div className="flex flex-col items-center p-6 bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-lg hover:shadow-lg transition-all duration-300">
                  <div className="w-16 h-16 bg-cyan-600 rounded-full flex items-center justify-center mb-4">
                    <BookOpen className="text-white" size={24} />
                  </div>
                  <h4 className="heading-6 text-text-primary mb-2">Greenfield GCC Setup</h4>
                  <p className="text-sm text-text-secondary text-center">Establishing investment operations functions from scratch - hiring, training, process design, and go-live</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {activeTab === 'achievements' && (
          <div className="space-y-8">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="liquid-glass glass-card p-6 border-l-4 border-l-amber-400">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mr-4">
                    <Award className="text-amber-600" size={24} />
                  </div>
                  <h3 className="heading-5 text-text-primary">Leadership Excellence</h3>
                </div>
                <ul className="space-y-2 text-text-secondary">
                  <li>• Leading 19+ professionals across 9 cross-functional teams</li>
                  <li>• Built Investment Division's CPS department from ground-up</li>
                  <li>• Consistent 98% engagement scores with 0% attrition</li>
                  <li>• Direct reporting relationship with Global MD</li>
                </ul>
              </Card>

              <Card className="liquid-glass glass-card p-6 border-l-4 border-l-cyan-400">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mr-4">
                    <Code className="text-cyan-600" size={24} />
                  </div>
                  <h3 className="heading-5 text-text-primary">Automation Impact</h3>
                </div>
                <ul className="space-y-2 text-text-secondary">
                  <li>• 40+ Alteryx workflows developed and deployed</li>
                  <li>• 10+ VBA automation projects implemented</li>
                  <li>• 1600+ FTE hours of capacity savings across organization</li>
                  <li>• 70% time reduction in key processes</li>
                </ul>
              </Card>

              <Card className="liquid-glass glass-card p-6 border-l-4 border-l-violet-400">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-violet-100 rounded-full flex items-center justify-center mr-4">
                    <BookOpen className="text-violet-600" size={24} />
                  </div>
                  <h3 className="heading-5 text-text-primary">Industry Recognition</h3>
                </div>
                <ul className="space-y-2 text-text-secondary">
                  <li>• Alteryx Foundation Micro-credential Certified</li>
                  <li>• Employee of the Month at BNY Mellon</li>
                  <li>• SME recognition for comprehensive training programs</li>
                  <li>• Early Adopter Program member at TIAA</li>
                </ul>
              </Card>

              <Card className="liquid-glass glass-card p-6 border-l-4 border-l-emerald-400">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mr-4">
                    <TrendingUp className="text-emerald-600" size={24} />
                  </div>
                  <h3 className="heading-5 text-text-primary">Business Impact</h3>
                </div>
                <ul className="space-y-2 text-text-secondary">
                  <li>• India AI Management Sponsor - chair of AI Mumbai Committee</li>
                  <li>• Successfully transitioned multiple teams US-to-India</li>
                  <li>• Built 4 new teams in Investment CoE from scratch</li>
                  <li>• Reduced manual processes by 1.5 FTE through VBA macros</li>
                </ul>
              </Card>
            </div>
          </div>
        )}
        </div>
        {/* End genie animation wrapper */}
        </div>
      </div>
    </section>
  );
};

export default PersonalSection;
